import {Card, FormControl, FormField, Separator} from 'platform/components';
import {Grid, GridItem, Space, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {useGetSeriesListQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {useDocumentTemplates} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {FormType} from '../../../types/ServiceOrderVariant';

interface InternalPaymentProps extends RequiredTestIdProps {
  formApi: UseFormReturn<FormType>;
  control: FormControl<FormType>;
  title: string;
}

export function InternalPayment(props: InternalPaymentProps) {
  const isAllowed = useWatch({
    control: props.control,
    name: 'paymentType.internalPayment.isAllowed',
  });

  const {options, isLoading} = useGetSeriesListQuery(
    {
      type: ['accounting/invoice'],
    },
    {
      selectFromResult: (result) => ({
        ...result,
        options:
          result?.data?.map((serie) => ({
            label: serie.name,
            value: serie.id,
          })) ?? [],
      }),
    }
  );

  const [getTemplates, {isLoading: isTemplatesLoading}] = useDocumentTemplates();
  const {options: invoiceTemplateOptions} = getTemplates('invoice');

  return (
    <Card
      title={props.title}
      isExpanded={isAllowed}
      control={{
        type: 'switch',
        value: isAllowed,
        onChange: (isEnabled) => {
          props.formApi.setValue('paymentType.internalPayment.isAllowed', isEnabled);
        },
      }}
      variant="inlineGrey"
      data-testid={suffixTestId('card', props)}
    >
      <Separator spacing={0} />
      <Space vertical={4} />
      <VStack spacing={4}>
        <Grid columns={2} horizontalSpacing={6}>
          <GridItem>
            <FormField
              control={props.control}
              name="paymentType.internalPayment.invoice.templateId"
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceTemplate')}
              isNotClearable
              options={invoiceTemplateOptions ?? []}
              isLoading={isTemplatesLoading}
              isRequired
              data-testid={suffixTestId('invoiceTemplate', props)}
            />
          </GridItem>
        </Grid>
        <Grid columns={2} horizontalSpacing={6}>
          <FormField
            control={props.control}
            name="paymentType.internalPayment.invoice.docSeriesId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.invoiceDocumentSerie')}
            isNotClearable
            options={options}
            isLoading={isLoading}
            isRequired
            data-testid={suffixTestId('invoiceDocumentSerie', props)}
          />
          <FormField
            control={props.control}
            name="paymentType.internalPayment.invoiceCorrection.docSeriesId"
            type="choice"
            label={i18n.t('entity.settings.labels.correctiveDocument')}
            isNotClearable
            options={options}
            isLoading={isLoading}
            isRequired
            data-testid={suffixTestId('correctiveDocument', props)}
          />
        </Grid>
      </VStack>
    </Card>
  );
}
